import { useState } from 'react';
import useErrorState from '../../../context/error/useErrorState';
import useModalState from '../../../context/modal/useModalState';
import { UserPositionRaw } from '../../../hooks/web3/trade/useUserPositions';
import { UserOrder } from '../../../hooks/web3/trade/useUserOpenOrders';
import Button from '../../../components/Button';
import clsx from 'clsx';
import ClosePositionWrapper from './ClosePositionWrapper';
import ClosePositionMarket, { ClosePositionMarketProps } from './ClosePositionMarket';
import ClosePositionLimit, { ClosePositionLimitProps } from './ClosePositionLimit';

export enum ClosePositionType {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

type RenderType<T> = {
    component: React.FC<T>;
    props: Omit<T, 'children'>;
};

type RenderClosePositionType = {
    [ClosePositionType.MARKET]: RenderType<ClosePositionMarketProps>;
    [ClosePositionType.LIMIT]: RenderType<ClosePositionLimitProps>;
};

type ClosePositionProps = {
    onClose: () => void;
};

const ClosePosition: React.FC<ClosePositionProps> = ({ onClose }) => {
    const { setError } = useErrorState();
    const { modalData, closeModal } = useModalState();

    const [positionType, setPositionType] = useState<ClosePositionType>(
        modalData.data.defaultEditTab || ClosePositionType.MARKET
    );
    const [orderCancelled, setOrderCancelled] = useState(false);

    const render: RenderClosePositionType = {
        MARKET: {
            component: ClosePositionMarket,
            props: {},
        },
        LIMIT: {
            component: ClosePositionLimit,
            props: { orderCancelled, setOrderCancelled },
        },
    };

    const userPosition: UserPositionRaw | UserOrder | undefined = modalData.data.position;
    const userOrder =
        userPosition && 'orderData' in userPosition ? userPosition : undefined;

    const handleConfirm = async (
        confirm: () => Promise<void>,
        validate: () => boolean
    ) => {
        try {
            if (!validate()) return;

            await confirm();

            // returning here because we don't want modal to close
            if (userOrder && !orderCancelled) return;

            onClose();
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <>
            <div className='position-type-buttons-container'>
                {Object.values(ClosePositionType).map((type) => {
                    const active = positionType === type;

                    return (
                        <Button
                            key={type}
                            className={clsx({ active })}
                            onClick={() => setPositionType(type)}
                            tabIndex={active ? -1 : 0}
                        >
                            {type.toLowerCase()}
                        </Button>
                    );
                })}
            </div>
            <ClosePositionWrapper
                component={render[positionType].component as React.FC}
                props={render[positionType].props}
            >
                {({ confirm, validate }) =>
                    userOrder && positionType === modalData.data.defaultEditTab ? (
                        <div className='action-buttons-container'>
                            <Button
                                onClick={() => handleConfirm(confirm, validate)}
                                disabled={orderCancelled}
                            >
                                Cancel Current Order
                            </Button>
                            <Button
                                onClick={() => handleConfirm(confirm, validate)}
                                disabled={!orderCancelled}
                            >
                                Update
                            </Button>
                        </div>
                    ) : (
                        <Button onClick={() => handleConfirm(confirm, validate)}>
                            Confirm
                        </Button>
                    )
                }
            </ClosePositionWrapper>
        </>
    );
};

export default ClosePosition;
